// src/lib/typenlisten.js
// --- ABHAENGIGE TYPENLISTEN: HERSTELLER -> TYPEN / BAUREIHEN ------------------
//
// Idee: Der Monteur waehlt zuerst den Hersteller, danach bekommt er nur die
// Baureihen DIESES Herstellers vorgeschlagen. Wie in auswahllisten.js gilt:
// alles sind VORSCHLAEGE, freier Text bleibt immer moeglich.
//
// Recherche am 13.09.2026 auf Herstellerseiten, Katalogen und Betriebsanleitungen.
// Aufgenommen wurde nur, was sich belegen liess. Ueber jedem Herstellerblock
// steht die Quelle. Wo eine Liste sicher unvollstaendig ist, steht es dabei.
//
// Felder:
//   alt: true  = Auslauf-/Altbaureihe (nicht mehr im Programm), kommt aber in
//                aelteren Stationen vor. Die Hilfsfunktionen liefern aktuelle
//                Baureihen zuerst, alte danach.
//   aliase     = andere Schreibweisen / fruehere Firmennamen, damit z. B.
//                "Betonbau (jetzt Gritec)" oder "ABB Calor Emag" gefunden werden.
//
// Isolierart der MS-Anlage:
//   'sf6'       = gasisoliert mit SF6
//   'clean_air' = gasisoliert, aber SF6-frei (Clean Air, trockene/synthetische
//                 Luft o. ae., z. B. 8DJH blue GIS, SafeRing Air, RM AirSeT)
//   'luft'      = luftisolierte Anlage (AIS), offen im Feld
//   'feststoff' = feststoffisoliert (Giessharz) mit Vakuumschaltern
//   null        = nicht sicher belegt

import { STATIONS_TYPEN } from './auswahllisten.js';

// ============================================================================
// 1) MITTELSPANNUNGS-SCHALTANLAGEN
// ============================================================================

export const MS_ANLAGEN = [
  // --- Siemens ---------------------------------------------------------------
  // https://www.siemens.com/en-us/products/energy-systems/8djh/
  // https://www.siemens.com/en-us/products/energy-systems/8djh36/
  // https://www.siemens.com/en-us/products/energy-systems/8djh12/
  // https://www.siemens.com/de/de/produkte/energie/mittelspannung/systeme/8djh24.html
  // https://assets.new.siemens.com/siemens/assets/api/uuid:60f3dbd7588876438a66d3503921eedeadc95ad2/8djhcompact-en-cataloge.pdf
  // https://assets.new.siemens.com/siemens/assets/api/uuid:cb802914-74a4-43e4-80fe-574209a48251/catalogue-simosec-new-de.pdf
  // Altbaureihen 8DJ10 / 8DJ20 / 8DH10 (8DH10 von Siemens selbst als "Auslauftype" gefuehrt):
  // https://silo.tips/download/8dj-8dh-gasisolierte-metallgekapselte-und-metallgeschottete-mittelspannungs-scha
  // https://w3.siemens.com/powerdistribution/global/de/mv/mittelspannungsschaltanlagen/gasisolierte-schaltanlagen-fuer-sekundaere-verteilungsnetze/seiten/8dh10-alt.aspx
  // NXAIR / NXPLUS C sind Primaertechnik, kommen aber in grossen Uebergabestationen vor:
  // https://assets.new.siemens.com/siemens/assets/api/uuid:655417ad-d2a9-4771-9c33-d8c747d2d75a/ha-25-73-de.pdf
  {
    hersteller: 'Siemens',
    aliase: ['Siemens AG', 'Siemens Energy'],
    typen: [
      { name: '8DJH',              isolierart: 'sf6',       alt: false },
      { name: '8DJH Compact',      isolierart: 'sf6',       alt: false },
      { name: '8DJH 36',           isolierart: 'sf6',       alt: false },
      { name: '8DJH 12 blue GIS',  isolierart: 'clean_air', alt: false },
      { name: '8DJH 24 blue GIS',  isolierart: 'clean_air', alt: false },
      { name: 'SIMOSEC',           isolierart: 'luft',      alt: false },
      { name: 'NXPLUS C',          isolierart: 'sf6',       alt: false },
      { name: 'NXAIR',             isolierart: 'luft',      alt: false },
      { name: '8DJ10',             isolierart: 'sf6',       alt: true },
      { name: '8DJ20',             isolierart: 'sf6',       alt: true },
      { name: '8DH10',             isolierart: 'sf6',       alt: true },
    ],
  },

  // --- ABB (inkl. frueher Calor Emag, Ratingen) -------------------------------
  // https://new.abb.com/medium-voltage/switchgear/gas-insulated/iec-gis-rmu-for-secondary-distribution/safering-safeplus
  // https://new.abb.com/medium-voltage/switchgear/air-insulated/iec-and-other-standards/iec-indoor-secondary-distribution-ais-unisec
  // https://new.abb.com/medium-voltage/switchgear/gas-insulated/iec-gis-rmu-for-secondary-distribution/iec-gas-insulated-ring-main-unit-safelink-cb
  // https://new.abb.com/medium-voltage/de/mittelspannungsprodukte/luftisolierte-mittelspannungsschaltanlagen/zs8-4-luftisolierte-schaltanlage
  // https://www.abb.com/global/en/company/about/history/heritage-brands/calor-emag
  // RGC und CTC: SF6-Vorgaenger, die ab 2000 von SafeRing/SafePlus abgeloest wurden.
  // SafeLink CB ist ein Produkt aus Indien, in Deutschland eher selten.
  {
    hersteller: 'ABB',
    aliase: ['ABB AG', 'Calor Emag', 'ABB Calor Emag'],
    typen: [
      { name: 'SafeRing',     isolierart: 'sf6',       alt: false },
      { name: 'SafePlus',     isolierart: 'sf6',       alt: false },
      { name: 'SafeRing 36',  isolierart: 'sf6',       alt: false },
      { name: 'SafePlus 36',  isolierart: 'sf6',       alt: false },
      { name: 'SafeRing Air', isolierart: 'clean_air', alt: false },
      { name: 'SafePlus Air', isolierart: 'clean_air', alt: false },
      { name: 'UniSec',       isolierart: 'luft',      alt: false },
      { name: 'UniSec Air',   isolierart: 'luft',      alt: false },
      { name: 'SafeLink CB',  isolierart: 'sf6',       alt: false },
      { name: 'ZS8.4',        isolierart: 'luft',      alt: false },
      { name: 'ZS8',          isolierart: 'luft',      alt: true },
      { name: 'RGC',          isolierart: 'sf6',       alt: true },
      { name: 'CTC',          isolierart: 'sf6',       alt: true },
    ],
  },

  // --- Schneider Electric (inkl. Sachsenwerk / Areva / Alstom, Regensburg) ---
  // https://www.se.com/de/de/product-category/87897-mittelspannungsschaltanlagen/
  // https://www.se.com/de/de/product-subcategory/87900-gasisolierte-schaltanlage-f%C3%BCr-die-sekund%C3%A4rverteilung/
  // https://www.se.com/de/de/about-us/company-profile/standorte/regensburg/
  // Areva-Sachsenwerk-Programm (FBX, FBE, GHA, GMA, WI):
  // https://www.slideserve.com/turner/areva-energietechnik-gmbh-sachsenwerk-mittelspannung
  // Sachsenwerk: ab 1936 AEG, 1996 Alstom, 2004 Areva, seit 2008 Schneider Electric.
  // GHA, GMA, WI, PIX sind Primaertechnik (nur grosse Uebergabestationen).
  // FBE (36 kV, Areva): Isolierart nicht sicher belegt.
  // Ringmaster (Schneider UK) bewusst weggelassen - im deutschen Netz praktisch nicht.
  {
    hersteller: 'Schneider Electric',
    aliase: ['Schneider', 'Merlin Gerin', 'Sachsenwerk', 'Areva', 'Alstom'],
    typen: [
      { name: 'RM6',          isolierart: 'sf6',       alt: false },
      { name: 'RM AirSeT',    isolierart: 'clean_air', alt: false },
      { name: 'FBX',          isolierart: 'sf6',       alt: false },
      { name: 'SM6-24',       isolierart: 'luft',      alt: false },
      { name: 'SM6-36',       isolierart: 'luft',      alt: false },
      { name: 'SM AirSeT',    isolierart: 'luft',      alt: false },
      { name: 'PremSet',      isolierart: 'feststoff', alt: false },
      { name: 'GM AirSeT',    isolierart: 'clean_air', alt: false },
      { name: 'GMA',          isolierart: 'sf6',       alt: false },
      { name: 'GHA',          isolierart: 'sf6',       alt: false },
      { name: 'WI',           isolierart: 'sf6',       alt: false },
      { name: 'PIX',          isolierart: 'luft',      alt: false },
      { name: 'FBE',          isolierart: null,        alt: true },
    ],
  },

  // --- Ormazabal (inkl. Felten & Guilleaume, beide Velatia) -------------------
  // https://www.ormazabal.com/en-gb/secondary-distribution-switchgears/
  // https://www.ormazabal.com/de-de/product/kompakte-transformatorstationen-und-kiosk-typ/  (CGC im miniBLOK)
  // F&G ist seit 2004 eine Marke der Velatia; F&G-Lastschaltanlage "GA 24" (SF6):
  // https://de.wikipedia.org/wiki/Felten_&_Guilleaume
  // https://patents.google.com/patent/DE3731442C2/de
  // Die heutige Ormazabal-Reihe "ga/gae630" fuehrt die GA/GAE-Linie weiter.
  {
    hersteller: 'Ormazabal',
    aliase: ['Felten & Guilleaume', 'Felten und Guilleaume', 'F&G', 'Velatia'],
    typen: [
      { name: 'ga',          isolierart: 'sf6',       alt: false },
      { name: 'gae630',      isolierart: 'sf6',       alt: false },
      { name: 'cgmcosmos',   isolierart: 'sf6',       alt: false },
      { name: 'cgm.3',       isolierart: 'sf6',       alt: false },
      { name: 'cgm.800',     isolierart: 'sf6',       alt: false },
      { name: 'cgm.zero24',  isolierart: 'clean_air', alt: false },
      { name: 'CGC',         isolierart: 'sf6',       alt: true },
      { name: 'GA 24',       isolierart: 'sf6',       alt: true },
      { name: 'GAE',         isolierart: 'sf6',       alt: true },
    ],
  },

  // --- Driescher (zwei getrennte Firmen!) -------------------------------------
  // DRIESCHER Wegberg - SF6-Anlagen MINEX und luftisolierte LDTM:
  // https://www.driescher-wegberg.de/index.html
  // https://www.driescher-wegberg.de/sf6-schaltanlagen.html
  // https://www.driescher-wegberg.de/luftisolierte-anlagen.html
  // https://www.manualslib.de/manual/169849/Driescher-Minex-C.html  (MINEX-C, SF6, bis 24 kV)
  // https://www.efa-on.de/exhibitors-products/product/efa-on/2442/950  (MINEX F-Gas frei, synthetische Luft)
  // ABS = Anti-Berst-System, ABSzero = ohne Druckwelle auf die Waende; beides SF6.
  // Driescher Elektrotechnische Werke Moosburg/Eisleben - Typen W24, D12, D24, F24:
  // https://www.driescher.de/produkte/mittelspannung/schaltanlagen/24-kv-schaltanlagen
  // https://www.driescher.de/images/product/schaltanlage_typ_w24_prospekt_26.pdf  (W 24 luftisoliert)
  // Isolierart von D12/D24/F24 nicht sicher belegt. "G.R.A.F." liess sich
  // NICHT belegen und fehlt deshalb.
  {
    hersteller: 'Driescher',
    aliase: ['Driescher Wegberg', 'Driescher Moosburg', 'Driescher Eisleben', 'Driescher Elektrotechnische Werke'],
    typen: [
      { name: 'MINEX ABS',        isolierart: 'sf6',       alt: false },
      { name: 'MINEX ABSzero',    isolierart: 'sf6',       alt: false },
      { name: 'MINEX-F ABS',      isolierart: 'sf6',       alt: false },
      { name: 'MINEX-F ABSzero',  isolierart: 'sf6',       alt: false },
      { name: 'MINEX F-Gas frei', isolierart: 'clean_air', alt: false },
      { name: 'LDTM',             isolierart: 'luft',      alt: false },
      { name: 'LDTM-B',           isolierart: 'luft',      alt: false },
      { name: 'LDTM-W',           isolierart: 'luft',      alt: false },
      { name: 'W24',              isolierart: 'luft',      alt: false },
      { name: 'D12',              isolierart: null,        alt: false },
      { name: 'D24',              isolierart: null,        alt: false },
      { name: 'F24',              isolierart: null,        alt: false },
      { name: 'MINEX-C',          isolierart: 'sf6',       alt: true },
      { name: 'MINEX',            isolierart: 'sf6',       alt: true },
    ],
  },

  // --- Eaton (Holec) -----------------------------------------------------------
  // https://www.eaton.com/ma/en-gb/catalog/medium-voltage-power-distribution-control-systems/xiria.html
  // https://www.eaton.com/nz/en-gb/catalog/medium-voltage-power-distribution-control-systems/magnefix.html
  // https://www.eaton.com/content/dam/eaton/products/medium-voltage-power-distribution-control-systems/iec-assemblies-mv-switchgear/magnefix/eaton-magnefix-user-manual-en-us.pdf
  // Magnefix: Giessharz + Luft, nur bis 15 kV (seit 1955, v. a. Niederlande).
  // Welche Magnefix-Bauformen (MA, MG, MY) ausgelaufen sind, war nicht belegbar.
  {
    hersteller: 'Eaton',
    aliase: ['EATON', 'Holec', 'Eaton Holec'],
    typen: [
      { name: 'Xiria',         isolierart: 'feststoff', alt: false },
      { name: 'Magnefix MD4',  isolierart: 'feststoff', alt: false },
      { name: 'Magnefix MF',   isolierart: 'feststoff', alt: false },
      { name: 'Magnefix MA',   isolierart: 'feststoff', alt: false },
      { name: 'Magnefix MG',   isolierart: 'feststoff', alt: false },
      { name: 'Magnefix MY',   isolierart: 'feststoff', alt: false },
    ],
  },

  // --- Wickmann (Witten / Boeblingen), WEVA heute Cellpack --------------------
  // https://www.driescher-wegberg.de/sanierung-wickmann-stationen.html  (Typ MBS)
  // https://www.esti.admin.ch/inhalte/pdf/MUB/Deutsch/2018-07_08_weva_d.pdf
  //   (WEVA 83, WEVA-C = WEVA Compact, WEVA-M = WEVA Modul, WEVA Satellit;
  //    Hersteller frueher Wickmann Energietechnik / Peyer, heute Cellpack Power Systems)
  // Isolierart der Alt-Anlagen nicht belegt. Wartung alter Wickmann-Anlagen: Energietechnik Kueper.
  {
    hersteller: 'Wickmann',
    aliase: ['Wickmann-Werke', 'Wickmann Energietechnik', 'Cellpack', 'Cellpack Power Systems'],
    typen: [
      { name: 'WEVA-C',       isolierart: null, alt: false },
      { name: 'MBS',          isolierart: null, alt: true },
      { name: 'WEVA 83',      isolierart: null, alt: true },
      { name: 'WEVA-M',       isolierart: null, alt: true },
      { name: 'WEVA Satellit', isolierart: null, alt: true },
    ],
  },

  // --- Ziegler Elektrotechnik --------------------------------------------------
  // Steht in MS_HERSTELLER (auswahllisten.js, aus den Altprotokollen). Eine
  // Typenreihe liess sich im Netz NICHT finden - Liste bewusst leer.
  {
    hersteller: 'Ziegler Elektrotechnik',
    aliase: ['Ziegler'],
    typen: [],
  },

  // --- Efacec (Portugal) - in Deutschland selten -------------------------------
  // https://www.efacec.com/business/switchgear/
  // evoFIX laut Hersteller SF6-frei gasisoliert ("available soon").
  {
    hersteller: 'Efacec',
    aliase: [],
    typen: [
      { name: 'Fluofix',  isolierart: 'sf6',       alt: false },
      { name: 'Normafix', isolierart: 'luft',      alt: false },
      { name: 'evoFIX',   isolierart: 'clean_air', alt: false },
    ],
  },

  // --- Lucy Electric (UK) - in Deutschland selten ------------------------------
  // https://www.lucyelectric.com/all-secondary-switchgear-products/ring-main-units/
  // https://www.lucyelectric.com/app/uploads/2020/09/Lucy-Electric-Sabre-_-0920.pdf
  {
    hersteller: 'Lucy Electric',
    aliase: ['Lucy'],
    typen: [
      { name: 'Sabre',       isolierart: 'sf6', alt: false },
      { name: 'Sabre VRN2a', isolierart: 'sf6', alt: false },
    ],
  },
];

// ============================================================================
// 2) TRANSFORMATOREN
// ============================================================================

export const TRAFO_TYPEN = [
  // --- SGB / SGB-SMIT (Starkstrom-Geraetebau, Regensburg) ---------------------
  // Oeltrafos haben bei SGB keinen Markennamen, sondern ein Typkuerzel auf dem
  // Leistungsschild, gefolgt von Leistung/Reihenspannung (z. B. "DOTE 400/20"):
  //   D = Drehstrom, O = Oel, T = Transformator, E/N/U/Z = Verlustliste,
  //   L = Leerlaufverluste vermindert. (Hermetikausfuehrung)
  // https://sgb-smit.pl/Broszury/Verteiltransformatoren.pdf  (Auswahltabelle "Typisierung", DM-Zeit)
  // Giessharz: Typkuerzel DTTHL (z. B. "DTTHL 630/20") und Baureihe GravityLine:
  // https://www.wmwag.com/de/datenblatt/1400020_SGB+Regensburg-DTTHL+63020.pdf
  // https://www.sgb-smit.com/fileadmin/user_upload/Downloads/Broschueren/Cast_Resin_CZ/GravityLine_DE.pdf
  // Unvollstaendig: weitere Giessharz-Kuerzel (z. B. ohne "L") nicht einzeln belegt.
  {
    hersteller: 'SGB',
    aliase: ['SGB-SMIT', 'SGB SMIT', 'Starkstrom-Gerätebau', 'Starkstrom Geraetebau'],
    typen: [
      { name: 'DOTE',        art: 'oel',       alt: false },
      { name: 'DOTEL',       art: 'oel',       alt: false },
      { name: 'DOTN',        art: 'oel',       alt: false },
      { name: 'DOTNL',       art: 'oel',       alt: false },
      { name: 'DOTU',        art: 'oel',       alt: false },
      { name: 'DOTUL',       art: 'oel',       alt: false },
      { name: 'DOTZ',        art: 'oel',       alt: false },
      { name: 'DOTZL',       art: 'oel',       alt: false },
      { name: 'DTTHL',       art: 'giessharz', alt: false },
      { name: 'GravityLine', art: 'giessharz', alt: false },
    ],
  },

  // --- Siemens / Siemens Energy ------------------------------------------------
  // https://www.siemens-energy.com/global/en/home/products-services/product/geafol-dry-type-distribution-transformers.html
  // https://www.siemens-energy.com/global/en/offerings/power-transmission/portfolio/transformers/fluid-immersed-distribution-transformers.html
  // https://pdfcoffee.com/siemens-oil-filled-distribution-transformers-tumetic-and-tunorma-pdf-free.html
  // TUMETIC = hermetisch, vollgefuellt; TUNORMA = mit Ausdehnungsgefaess.
  // Heute laufen die Oeltrafos unter "FITformer" ("Tumetic design") - daher alt.
  {
    hersteller: 'Siemens',
    aliase: ['Siemens AG', 'Siemens Energy'],
    typen: [
      { name: 'FITformer',     art: 'oel',       alt: false },
      { name: 'FITformer REG', art: 'oel',       alt: false },
      { name: 'GEAFOL',        art: 'giessharz', alt: false },
      { name: 'GEAFOL Neo',    art: 'giessharz', alt: false },
      { name: 'TUMETIC',       art: 'oel',       alt: true },
      { name: 'TUNORMA',       art: 'oel',       alt: true },
    ],
  },

  // --- Trafo-Union (Transformatoren Union AG, 1969 von AEG + Siemens gegruendet,
  //     1987 in Siemens eingegliedert) ----------------------------------------
  // https://www.nordbayern.de/2.5886/stichwort-trafowerk-1.711922
  // Baureihen wie bei Siemens (TUMETIC, TUNORMA, GEAFOL).
  // AEG selbst: eigene Baureihennamen liessen sich nicht belegen - AEG-Trafos
  // aus der Zeit ab 1969 tragen meist "Trafo-Union" auf dem Schild.
  {
    hersteller: 'Trafo Union',
    aliase: ['Trafo-Union', 'Transformatoren Union', 'AEG Trafo-Union'],
    typen: [
      { name: 'TUMETIC', art: 'oel',       alt: true },
      { name: 'TUNORMA', art: 'oel',       alt: true },
      { name: 'GEAFOL',  art: 'giessharz', alt: true },
    ],
  },

  // --- ABB / Hitachi Energy -----------------------------------------------------
  // https://www.hitachienergy.com/products-and-solutions/transformers/distribution-transformers/dry-type-transformers/resibloc-transformers
  // https://www.hitachienergy.com/us/en/products-and-solutions/transformers/distribution-transformers/dry-type-transformers
  // RESIBLOC seit 1974. Fuer Oel-Verteiltrafos gibt es keinen belegten
  // Baureihennamen (nur Marken wie EconiQ/TXpert) - Oel-Liste bleibt leer.
  {
    hersteller: 'ABB',
    aliase: ['ABB AG', 'Hitachi Energy', 'Hitachi ABB'],
    typen: [
      { name: 'RESIBLOC',               art: 'giessharz', alt: false },
      { name: 'Vacuum Cast Coil (VCC)', art: 'giessharz', alt: false },
    ],
  },

  // --- Schneider Electric --------------------------------------------------------
  // https://www.se.com/uk/en/product-category/3600-medium-voltage-transformers/
  // https://www.se.com/de/de/product-range-presentation/62109-transformator-minera-sgrid/
  // Minera hat viele Unterreihen (MP, HE+, R, E, SP, PV ...); hier nur die Grundnamen.
  {
    hersteller: 'Schneider Electric',
    aliase: ['Schneider'],
    typen: [
      { name: 'Minera',       art: 'oel',       alt: false },
      { name: 'Minera SGrid', art: 'oel',       alt: false },
      { name: 'Vegeta',       art: 'oel',       alt: false },
      { name: 'Trihal',       art: 'giessharz', alt: false },
    ],
  },

  // --- France Transfo (seit 1994 Schneider Electric) ----------------------------
  // https://www.pappers.fr/entreprise/ft-france-transfo-357801109
  // http://eleectrotechnique.fr/wp-content/uploads/2013/08/trihal-fiche-technique-ff26p.pdf
  {
    hersteller: 'France Transfo',
    aliase: [],
    typen: [
      { name: 'Trihal', art: 'giessharz', alt: false },
    ],
  },

  // --- Heim ------------------------------------------------------------------------
  // Nur Typkuerzel aus Gebrauchttrafo-Angeboten belegt: "DO 1250" (Oel), "DTG 630" (Giessharz).
  // http://www.sonn-elektrotechnik.de/transformatoren/gebrauchter-transformator/trafo-Heim-DO1250-64177.html
  // http://www.sonn-elektrotechnik.de/transformatoren/gebrauchter-transformator/trafo-Heim-DTG_630-70983.html
  {
    hersteller: 'Heim',
    aliase: [],
    typen: [
      { name: 'DO',  art: 'oel',       alt: true },
      { name: 'DTG', art: 'giessharz', alt: true },
    ],
  },

  // --- Pauwels (Mechelen, Belgien; zwischenzeitlich CG Power) -------------------
  // https://www.pauwels.com/products/
  // Keine Baureihennamen fuer Verteiltrafos belegbar - Liste leer.
  { hersteller: 'Pauwels', aliase: ['Pauwels Trafo', 'CG Power'], typen: [] },

  // --- TEK -------------------------------------------------------------------------
  // Unklar, welche Firma in den Altprotokollen gemeint ist (TEK Trafo-Elektronik-
  // Kirchner, Lohfelden, oder TEK Transformatör, Tuerkei). Keine Baureihen belegt.
  { hersteller: 'TEK', aliase: [], typen: [] },
];

// ============================================================================
// 3) STATIONSGEBAEUDE
// ============================================================================

export const STATIONS_TYPEN_JE_HERSTELLER = [
  // --- Gritec (bis Februar 2024: Betonbau) --------------------------------------
  // Kompakt (UK/UKL): Liste aus auswahllisten.js (STATIONS_TYPEN), bestaetigt durch
  // https://www.gritec.com/de/technikgebaeude/stationen/kompaktstationen/
  // https://gritec.com/default-wAssets/docs/downloadcenter/technische-datenblaetter/de_NEU/Technisches_Datenblatt_UK_3615-Muster.pdf
  // Begehbar (UF-Serien):
  // https://www.gritec.com/de/technikgebaeude/stationen/begehbare-stationen/
  {
    hersteller: 'Gritec',
    aliase: ['Betonbau', 'Betonbau (jetzt Gritec)'],
    typen: [
      ...STATIONS_TYPEN,
      'UF 18', 'UF 25', 'UF 27', 'UF 30', 'UF 33', 'UF 36', 'UF 39', 'UF 42',
    ],
  },

  // --- SCHEIDT (Rinteln) --------------------------------------------------------
  // https://www.scheidt.de/en/products/turnkey-stations/scheidt-system-nz/
  // NZ = nicht begehbar, Zellenbauweise; NEK = nicht begehbar, Elementbau mit Keller;
  // BE = begehbar ohne Keller; BEK = begehbar mit Keller.
  { hersteller: 'Scheidt', aliase: ['SCHEIDT'], typen: ['NZ', 'NEK', 'BE', 'BEK'] },

  // --- DRIESCHER Wegberg (Kunststoff- und Betonstationen) -----------------------
  // https://www.driescher-wegberg.de/index.html
  {
    hersteller: 'Driescher',
    aliase: ['Driescher Wegberg'],
    typen: [
      'K', 'KSP', 'KST', 'KSC', 'KST begehbar',          // Kunststoff
      'BSP', 'BST385', 'BST435/1', 'BST435/2', 'BST485', // Beton
    ],
  },

  // --- Ernst Wirth Fertigbau -----------------------------------------------------
  // https://www.wirth-fertigbau.de/kompakt-trafostationen/
  // W 3124 stand nur im Suchtreffer-Auszug der Herstellerseite, nicht im Seitentext.
  // Begehbare Stationen fertigt Wirth individuell, ohne Typnamen.
  { hersteller: 'Ernst Wirth', aliase: ['Wirth', 'Wirth Fertigbau'], typen: ['W 2817', 'W 3119', 'W 3124'] },

  // --- ABB ------------------------------------------------------------------------
  // https://www.abb.com/global/en/areas/electrification/medium-voltage/packaging-solutions/integrated-secondary-solutions/unipack
  // UniPack-S = Stahlgehaeuse, UniPack-G = GFK-Gehaeuse.
  { hersteller: 'ABB', aliase: ['ABB AG'], typen: ['UniPack-S', 'UniPack-G'] },

  // --- Ormazabal -------------------------------------------------------------------
  // https://www.ormazabal.com/de-de/product/kompakte-transformatorstationen-und-kiosk-typ/
  // https://www.directindustry.com/prod/ormazabal/product-26707-670811.html
  { hersteller: 'Ormazabal', aliase: [], typen: ['PFU', 'miniBLOK'] },

  // --- Ohne belegbare Typenreihen (Liste leer, Freitext) --------------------------
  // Groetz (Gaggenau): Fertigteil-Trafostationen und Systemgebaeude, individuell.
  //   https://www.groetz-fertiggaragen.de/trafo-technikraeume/trafostation
  // Wagner Schaltanlagen: Kompaktstationen Stahl/Beton nach Kundenwunsch.
  //   https://www.wagner-schaltanlagen.de/leistungen/trafostationen/
  // Kalchschmid: ueberwiegend Individualstationen.
  //   https://www.kalchschmid-trafostation.com/
  // Horstmann & Schwarz: vor allem Vermietung von Stationen, keine Baureihen.
  //   https://horstmann-schwarz.de/trafostationen/
  { hersteller: 'Grötz',                aliase: ['Groetz'],                 typen: [] },
  { hersteller: 'Wagner Schaltanlagen', aliase: ['Wagner'],                 typen: [] },
  { hersteller: 'Kalchschmid',          aliase: [],                         typen: [] },
  { hersteller: 'Horstmann & Schwarz',  aliase: ['Horstmann und Schwarz'],  typen: [] },
];

// ============================================================================
// 4) SPANNUNGEN
// ============================================================================
// Schreibweise wie in auswahllisten.js: Dezimalkomma ('0,4'), sonst reine Zahl.
//
// Betriebsspannung (Netz-Nennspannung Un) vs. Bemessungsspannung der Anlage Ur:
//   6 kV -> 7,2 kV | 10 kV -> 12 kV | 15 kV -> 17,5 kV | 20 kV -> 24 kV | 30 kV -> 36 kV
// Region Karlsruhe:
//   Stadtwerke Karlsruhe Netzservice: reines 20-kV-Kabelnetz (~1.650 Stationen)
//     https://www.netzservice-swka.de/netze/strom/mittelspannung.php
//   Netze BW: Nennspannungen 10, 20 und 30 kV (20 kV am haeufigsten)
//     https://assets.ctfassets.net/xytfb1vrn7of/6GDkuDxty3GBA9EwLM6YFC/516ad2d9e6837d0c660b629510ab7903/TAB_Mittelspannung_2019.pdf
// 25 kV nur der Vollstaendigkeit halber - im oeffentlichen Netz in BW nicht belegt.

export const MS_BETRIEBSSPANNUNG_KV = ['20', '10', '30', '6', '15', '25'];
export const MS_BEMESSUNGSSPANNUNG_KV = ['24', '12', '36', '7,2', '17,5'];

// Niederspannung in Volt: 400 V = Netz 230/400 V, 420 V = Leerlaufspannung
// der Verteiltrafos, 690 V = Industrie / Wind / PV.
export const NS_SPANNUNG_V = ['400', '420', '690'];

// ============================================================================
// 5) HILFSFUNKTIONEN
// ============================================================================

// Kleinschreibung, Umlaute ausschreiben, alles ausser Buchstaben/Ziffern zu Leerzeichen.
function normiere(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Fuer Typvergleiche zusaetzlich ohne Leerzeichen ("8DJH-36" == "8djh 36").
function kompakt(text) {
  return normiere(text).replace(/ /g, '');
}

// Findet den passenden Herstellereintrag, tolerant:
//  - "Siemens AG" findet "Siemens" (Name als ganze Woerter enthalten)
//  - "Betonbau (jetzt Gritec)" findet Gritec (ueber Alias)
//  - "Schneider" findet "Schneider Electric" (Anfang des Namens)
// Bei mehreren Treffern gewinnt der laengste (spezifischste) Name,
// z. B. "ABB Calor Emag" -> Alias "abb calor emag".
function findeHersteller(liste, hersteller) {
  const eingabe = normiere(hersteller);
  if (!eingabe) return null;
  const eingabeMitRand = ` ${eingabe} `;
  let bester = null;
  let besteWertung = 0;
  for (const eintrag of liste) {
    const schluessel = [eintrag.hersteller, ...(eintrag.aliase || [])].map(normiere).filter(Boolean);
    for (const s of schluessel) {
      let wertung = 0;
      if (eingabeMitRand.includes(` ${s} `)) wertung = 1000 + s.length;
      else if (eingabe.length >= 3 && s.startsWith(eingabe)) wertung = eingabe.length;
      if (wertung > besteWertung) { besteWertung = wertung; bester = eintrag; }
    }
  }
  return bester;
}

// Aktuelle Baureihen zuerst, Altbaureihen dahinter, ohne Doppelte.
function namenSortiert(typen) {
  const gesehen = new Set();
  const ergebnis = [];
  for (const t of [...typen.filter(t => !t.alt), ...typen.filter(t => t.alt)]) {
    const k = kompakt(t.name);
    if (gesehen.has(k)) continue;
    gesehen.add(k);
    ergebnis.push(t.name);
  }
  return ergebnis;
}

// Typen/Baureihen der MS-Anlage fuer einen Hersteller. Unbekannt -> [].
export function typenFuerMsHersteller(hersteller) {
  const eintrag = findeHersteller(MS_ANLAGEN, hersteller);
  return eintrag ? namenSortiert(eintrag.typen) : [];
}

// Isolierart zu einem getippten Typ. Erst exakter Treffer, sonst die laengste
// bekannte Baureihe, mit der der Typ beginnt ("8DJH RRT" -> 8DJH -> 'sf6').
// Ist der Hersteller unbekannt oder leer, wird ueber alle Hersteller gesucht.
// Nichts gefunden -> null.
export function isolierartFuerMsTyp(hersteller, typ) {
  const t = kompakt(typ);
  if (!t) return null;
  const eintrag = findeHersteller(MS_ANLAGEN, hersteller);
  const liste = eintrag ? [eintrag] : MS_ANLAGEN;
  let bester = null;
  let besteLaenge = 0;
  for (const e of liste) {
    for (const ty of e.typen) {
      const k = kompakt(ty.name);
      if (!k) continue;
      if (k === t) return ty.isolierart;
      if (k.length >= 3 && t.startsWith(k) && k.length > besteLaenge) {
        bester = ty;
        besteLaenge = k.length;
      }
    }
  }
  return bester ? bester.isolierart : null;
}

// Trafo-Baureihen eines Herstellers, optional gefiltert nach Art
// ('oel' / 'giessharz'; "Öl" und "Gießharz" werden ebenfalls verstanden).
export function typenFuerTrafoHersteller(hersteller, art) {
  const eintrag = findeHersteller(TRAFO_TYPEN, hersteller);
  if (!eintrag) return [];
  const gewuenscht = normiere(art).replace(/ /g, '');
  const typen = gewuenscht ? eintrag.typen.filter(t => t.art === gewuenscht) : eintrag.typen;
  return namenSortiert(typen);
}

// Stationstypen eines Gebaeude-Herstellers. Unbekannt -> [].
export function stationsTypenFuer(hersteller) {
  const eintrag = findeHersteller(STATIONS_TYPEN_JE_HERSTELLER, hersteller);
  return eintrag ? [...new Set(eintrag.typen)] : [];
}
